/**
 * 解析用户菜单
 * 匹配对应的路由
 */
import type { RouteRecordNormalized, RouteRecordRaw } from 'vue-router'
import type { MenuRecord } from '@/api/user/model'
import type { Role } from '@/api/account-management/model.d'
import { getAuthCache } from '@/utils/auth'
import { BOOLEAN } from '@/utils/constants'
import { ROLE_KEY } from '@/enums/cache'
import router from '../index'
import type { MenuRoute } from './type.d'
import {
  MENU_LABEL,
  ROUTE_ROLE_TYPE_NAME,
  isFirstMoneyBothMenuName,
  matchMenuOfRoleType
} from './constants'

/** 为菜单列表匹配路由 */
export function matchMenu(menus: MenuRecord[]) {
  const matcher = new MenuMatcher().init()
  const result = matcher.match(menus)
  return result
}
/** 菜单路由匹配器 */
class MenuMatcher {
  /** 路由表，以[meta.title]为索引 */
  routeMap = new Map<string, RouteRecordNormalized>()
  init() {
    const routes = router.getRoutes()
    routes.forEach((route) => {
      const title = route.meta.title as string
      this.routeMap.set(title, route)
    })
    return this
  }
  /** 匹配菜单 */
  match(menus: MenuRecord[]) {
    const output: MenuRoute[] = []
    const roleTypeName = getAuthCache<Role>(ROLE_KEY).roleTypeName // 角色大类名称
    menus.forEach((item) => {
      const { routeMap } = this
      const { name, permission, child } = item
      if (permission === BOOLEAN.NO) return // 无菜单权限
      const route = routeMap.get(name)
      // 匹配路由
      if (route) {
        // 结算系统 特殊路由，特殊处理 ---【ID1066429】删除相关菜单入口
        if (name === MENU_LABEL.LEYOPAY) return
        const menuOfRoleType = route.meta.menuOfRoleType as string[]
        if (!matchMenuOfRoleType(menuOfRoleType, roleTypeName)) return // 无路由权限
        const menuRoute: MenuRoute = { ...item, route, child: [] }
        if (Array.isArray(child)) {
          menuRoute.child = this.match(child)
        }
        output.push(menuRoute)
      }
    })
    return output
  }
  /**
   * 针对结算系统的菜单特殊处理逻辑
   * @param leyoPayMenu 结算系统菜单项
   * @returns 用于渲染的结算系统菜单
   */
  leyoPay(leyoPayMenu: MenuRecord) {
    // 1. 获取细分角色大类：【首推供应商&YQS供应商】/【YQS供应商】
    let menuRole = ROUTE_ROLE_TYPE_NAME.MONEY_TREE_ONLY
    const first = leyoPayMenu.child.find((item) => isFirstMoneyBothMenuName(item.name))
    if (first && first.permission === BOOLEAN.YES) menuRole = ROUTE_ROLE_TYPE_NAME.FIRST_MONEY_BOTH
    // 2.1 构造【自助结算】菜单
    const settlementRoute = this.routeMap.get(MENU_LABEL.LEYOPAY) as RouteRecordNormalized
    const settlementMenuRoute: MenuRoute = {
      ...leyoPayMenu,
      name: MENU_LABEL.SETTLEMENT,
      id: 100 + leyoPayMenu.id,
      route: settlementRoute,
      child: this.route2Menu(settlementRoute.children, menuRole)
    }
    // 2.1 构造【我的品种】菜单
    const varityRoute = this.routeMap.get(MENU_LABEL.VARITY) as RouteRecordNormalized
    const varityMenuRoute: MenuRoute = {
      ...leyoPayMenu,
      name: MENU_LABEL.VARITY,
      id: 200 + leyoPayMenu.id,
      route: varityRoute as RouteRecordNormalized,
      child: this.route2Menu(varityRoute.children, menuRole)
    }
    return [settlementMenuRoute, varityMenuRoute]
  }
  /**
   * 从路由中生产菜单项
   * @param routes 路由列表
   * @param menuRole 角色大类名称
   */
  route2Menu(routes: RouteRecordRaw[] = [], menuRole: string) {
    const output: MenuRoute[] = []
    routes.forEach((item, id) => {
      const menuOfRoleType = item.meta?.menuOfRoleType as string[]
      if (!matchMenuOfRoleType(menuOfRoleType, menuRole)) return // 无路由权限
      const title = item.meta?.title as string
      const route = item as RouteRecordNormalized
      // 构造菜单项
      const menuRoute = { id, route, name: title } as unknown as MenuRoute
      output.push(menuRoute)
    })
    return output
  }
}
